import { useState, useMemo, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Shield } from 'lucide-react';
import type { ExternalUserFormState } from '../../hooks/useExternalUserFormWizard';
import { ROLE_TYPE_LABELS } from '../../types/external-user.types';
import { DEFAULT_PERMISSIONS_BY_ROLE, getPermissionsForUserType } from '../../constants/permissions';
import { PermissionsSelector } from '@/Components/shared/PermissionsSelector';
import { FormSelect } from '@/Components/shared/FormSelect';
import { Tooltip } from '@/Components/ui/Tooltip';
import { ConfirmDialogWithOptions } from '@/Components/shared/ConfirmDialogWithOptions';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { useSocietyChildren, useSociety } from '@/features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useIsWarrantyPolicyOwner } from '@/features/warranties/hooks/useIsWarrantyPolicyOwner';
import type { WizardStepHandle } from './types';

interface Step2RoleProps {
  formData: ExternalUserFormState;
  updateFormData: (updates: Partial<ExternalUserFormState>) => void;
  onSubmit: (sendWelcomeEmail: boolean) => void;
  isSubmitting: boolean;
  onCanProceedChange?: (canProceed: boolean) => void;
}

export const Step2Role = forwardRef<WizardStepHandle, Step2RoleProps>(({
  formData,
  updateFormData,
  onSubmit,
  isSubmitting,
  onCanProceedChange,
}, ref) => {
  const { currentUser } = useAuthStore();
  const [showCustomPermissions, setShowCustomPermissions] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const { data: primarySociety } = useSociety(formData.primarySocietyId);
  const { data: effectiveConfig } = useEffectiveConfiguration(formData.primarySocietyId || undefined);
  const { data: childrenData } = useSocietyChildren(formData.primarySocietyId || '');
  const hasChildren = useMemo(() => {
    return (childrenData || []).length > 0;
  }, [childrenData]);
  const isWarrantyPolicyOwner = useIsWarrantyPolicyOwner(formData.primarySocietyId);

  const availablePermissions = useMemo(() => {
    const sid = formData.primarySocietyId;
    const allPermissions = getPermissionsForUserType('EXTERNAL', primarySociety?.vendorType, effectiveConfig ? {
      deliveryProofCreate: effectiveConfig.deliveryProofCreate,
      defineProvision: effectiveConfig.defineProvision,
      guarantorId: effectiveConfig.guarantorId,
      isTransferPrice: effectiveConfig.isTransferPrice,
      isWarrantyPolicyOwner,
      billedId: effectiveConfig.billedId,
      ownSocietyId: sid,
    } : undefined);

    const shouldHideSocietyPermissions = !hasChildren && formData.additionalSocietyIds.length === 0;

    if (shouldHideSocietyPermissions) {
      return allPermissions.filter(perm => perm.category !== 'society');
    }

    return allPermissions;
  }, [hasChildren, formData.additionalSocietyIds, formData.primarySocietyId, primarySociety?.vendorType, effectiveConfig?.deliveryProofCreate, effectiveConfig?.defineProvision, effectiveConfig?.guarantorId, effectiveConfig?.isTransferPrice, isWarrantyPolicyOwner, effectiveConfig?.billedId]);

  const defaultPermissionsForRole = useMemo(() => {
    const defaultPerms = DEFAULT_PERMISSIONS_BY_ROLE[formData.roleType] || [];
    const availableCodes = availablePermissions.map(p => p.code);
    return defaultPerms.filter(code => availableCodes.includes(code));
  }, [formData.roleType, availablePermissions]);

  const handleRoleChange = (roleType: ExternalUserFormState['roleType']) => {
    const defaultPerms = DEFAULT_PERMISSIONS_BY_ROLE[roleType] || [];
    const availableCodes = availablePermissions.map(p => p.code);
    const filteredPerms = defaultPerms.filter(code => availableCodes.includes(code));

    updateFormData({
      roleType,
      position: ROLE_TYPE_LABELS[roleType],
      permissions: filteredPerms,
    });
  };

  const handlePermissionsChange = (permissions: string[]) => {
    updateFormData({ permissions });
  };

  const handleToggleCustom = () => {
    if (!showCustomPermissions) {
      setShowCustomPermissions(true);
    } else {
      setShowCustomPermissions(false);
      updateFormData({ permissions: defaultPermissionsForRole });
    }
  };

  const handleResetPermissions = () => {
    updateFormData({ permissions: defaultPermissionsForRole });
    setShowCustomPermissions(false);
  };

  const handleSubmitClick = () => {
    if (formData.permissions.length === 0) {
      return;
    }
    setShowDialog(true);
  };

  const handleConfirmWithEmail = () => {
    setShowDialog(false);
    onSubmit(true);
  };

  const handleConfirmWithoutEmail = () => {
    setShowDialog(false);
    onSubmit(false);
  };

  const handleCancel = () => {
    setShowDialog(false);
  };

  const isInternalUser = currentUser?.type === 'INTERNAL';
  const canProceed = formData.permissions.length > 0 && !isSubmitting;

  // Remonter canProceed au parent
  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  useImperativeHandle(ref, () => ({
    handleNext: handleSubmitClick,
  }));

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-4" data-testid="step-3-role">
      <div className="tw-p-6 tw-space-y-5">
        {/* Heading unique */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
          <Shield className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Rôle & Permissions</h4>
        </div>

        {/* Carte 1 - Rôle */}
        <div id="external-user-role-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Définissez le rôle de l'utilisateur<Tooltip content="Le rôle détermine le niveau d'accès par défaut et le poste de l'utilisateur" /></p>
          <FormSelect
            value={formData.roleType}
            onChange={(value) => handleRoleChange(value as ExternalUserFormState['roleType'])}
            options={Object.entries(ROLE_TYPE_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
            label="Rôle"
            required
            icon={<Shield className="tw-w-5 tw-h-5" />}
            tooltip="Sélectionnez le rôle qui correspond au poste de l'utilisateur. Chaque rôle attribue des permissions par défaut."
            dataTestId="select-role-type"
          />
          <p className="tw-mt-2 tw-text-xs tw-text-gray-500">
            {{
              ADMIN: 'Administrateur : accès complet à toutes les fonctionnalités de la société',
              ACCOUNTANT: 'Comptable : accès aux factures, statistiques et consultation des contrats',
              SALES_MANAGER: 'Responsable commercial : gestion des contrats, garanties, utilisateurs et commissions',
              SALES: 'Commercial : création et suivi des contrats et garanties',
              SAV_MANAGER: 'Responsable SAV : gestion complète des dossiers SAV et suivi des contrats',
            }[formData.roleType]}
          </p>
        </div>

        {/* Carte 2 - Permissions */}
        <div id="external-user-permissions-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Configurez les permissions de l'utilisateur<Tooltip content="Les permissions définissent précisément les actions que l'utilisateur peut effectuer" /></p>

          {!showCustomPermissions ? (
            <div>
              <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
                Permissions par défaut pour le rôle{' '}
                <strong>{ROLE_TYPE_LABELS[formData.roleType]}</strong>{' '}
                ({defaultPermissionsForRole.length} permissions)
              </p>
              <button
                data-testid="btn-customize-permissions"
                type="button"
                onClick={handleToggleCustom}
                className="tw-px-4 tw-py-2 tw-text-sm tw-text-affinicar-primary tw-bg-affinicar-lighter tw-border tw-border-affinicar-light tw-rounded-xl hover:tw-bg-affinicar-light tw-transition-colors"
              >
                Personnaliser les permissions
              </button>
            </div>
          ) : (
            <div>
              <div className="tw-mb-3">
                <button
                  type="button"
                  onClick={handleResetPermissions}
                  className="tw-text-sm tw-text-gray-600 hover:tw-text-gray-900"
                >
                  Réinitialiser aux permissions par défaut
                </button>
              </div>
              <PermissionsSelector
                availablePermissions={availablePermissions}
                selectedPermissions={formData.permissions}
                onChange={handlePermissionsChange}
                showPermissionCode={false}
              />
              {formData.permissions.length === 0 && (
                <p className="tw-mt-2 tw-text-sm tw-text-red-600">
                  Veuillez sélectionner au moins une permission
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialogWithOptions
        isOpen={showDialog}
        title="Créer l'utilisateur externe"
        message={
          isInternalUser
            ? "Voulez-vous créer cet utilisateur ?"
            : "Voulez-vous créer cet utilisateur ?\n\nUn email avec les identifiants de connexion sera automatiquement envoyé à l'adresse indiquée."
        }
        confirmLabel={isInternalUser ? "Créer et envoyer l'email" : "Créer l'utilisateur"}
        alternativeLabel={isInternalUser ? "Créer sans envoyer l'email" : undefined}
        cancelLabel="Annuler"
        variant="info"
        onConfirm={handleConfirmWithEmail}
        onAlternative={isInternalUser ? handleConfirmWithoutEmail : undefined}
        onCancel={handleCancel}
      />
    </div>
  );
});

Step2Role.displayName = 'Step2Role';
